/**
 * 媒體庫的虛擬資料夾樹（呈現層）。
 *
 * 「資料夾」不是真的目錄——媒體庫的物件 key 是 sha256，儲存層完全扁平。樹是
 * 由標籤（`0/zh/4_food`）在 CMS 端切出來的視圖；怎麼切見 {@link buildMediaTree}，
 * 這裡只負責畫。
 *
 * **展開狀態是推導出來的，不是存出來的**：`expandOverrides` 只記使用者按過三角形
 * 的節點，其餘一律回退到預設規則（見 {@link isExpanded}）。這樣 facets 還在載入
 * 時不需要「等資料到了再 seed 一次初始狀態」的 effect，資料變動也不會讓已展開的
 * 節點莫名收起來。
 */

import { useEffect, useState } from 'react';

import type { TagFacet } from '../lib/api-client';
import { buildMediaTree, type FolderNode } from '../lib/media-tree';

/** 目前選取的節點；決定送給後端的篩選條件。 */
export type MediaSelection =
  | { kind: 'all' }
  | { kind: 'folder'; path: string }
  | { kind: 'label'; tag: string }
  | { kind: 'untagged' };

/** 預設展開到第幾層。1 = 展開根資料夾（看得到 `0` 底下的 en/jp/zh），再深就收著。 */
const DEFAULT_EXPAND_DEPTH = 1;

interface MediaFolderTreeProps {
  facets: { tags: TagFacet[]; untagged: number; total: number } | undefined;
  selection: MediaSelection;
  onSelect: (s: MediaSelection) => void;
}

/** `0/zh/4_food` → {`0`, `0/zh`, `0/zh/4_food`}；用來把選取項所在的路徑攤開。 */
function pathChain(path: string): Set<string> {
  const segments = path.split('/');
  return new Set(segments.map((_, i) => segments.slice(0, i + 1).join('/')));
}

export function MediaFolderTree({
  facets,
  selection,
  onSelect,
}: MediaFolderTreeProps): JSX.Element {
  const [expandOverrides, setExpandOverrides] = useState<Map<string, boolean>>(new Map());

  const selectedPath = selection.kind === 'folder' ? selection.path : null;
  const onPath = selectedPath === null ? new Set<string>() : pathChain(selectedPath);

  /**
   * 選取變更時，清掉沿途節點的手動狀態。
   *
   * 少了這段，「先收合 `0/zh`，再點某一列的 `📁 0/zh/4_food` 跳過去」會讓選取
   * 落在一個看不見的節點上——使用者按了跳轉卻什麼都沒發生。手動收合的意圖只在
   * 使用者自己沒有要求跳進去時才成立。
   */
  useEffect(() => {
    if (selectedPath === null) return;
    setExpandOverrides((prev) => {
      const chain = pathChain(selectedPath);
      if (![...chain].some((p) => prev.has(p))) return prev;
      const next = new Map(prev);
      for (const p of chain) next.delete(p);
      return next;
    });
  }, [selectedPath]);

  const isExpanded = (path: string, depth: number): boolean =>
    expandOverrides.get(path) ?? (depth <= DEFAULT_EXPAND_DEPTH || onPath.has(path));

  const toggleExpand = (path: string, current: boolean): void => {
    setExpandOverrides((prev) => new Map(prev).set(path, !current));
  };

  if (!facets) return <aside style={SIDEBAR}>載入中…</aside>;
  const tree = buildMediaTree(facets.tags);

  return (
    <aside style={SIDEBAR}>
      <TreeRow
        label="全部素材"
        icon="📁"
        count={facets.total}
        depth={0}
        active={selection.kind === 'all'}
        onClick={() => onSelect({ kind: 'all' })}
      />
      {tree.folders.map((f) => (
        <FolderRows
          key={f.path}
          node={f}
          depth={1}
          selection={selection}
          onSelect={onSelect}
          isExpanded={isExpanded}
          onToggleExpand={toggleExpand}
        />
      ))}

      {tree.labels.length > 0 && <Divider>標記</Divider>}
      {tree.labels.map((l) => (
        <TreeRow
          key={l.tag}
          label={l.tag}
          icon="🏷"
          count={l.count}
          depth={1}
          active={selection.kind === 'label' && selection.tag === l.tag}
          onClick={() => onSelect({ kind: 'label', tag: l.tag })}
        />
      ))}

      {facets.untagged > 0 && (
        <>
          <Divider>其他</Divider>
          <TreeRow
            label="未分類"
            icon="⚠"
            count={facets.untagged}
            depth={1}
            active={selection.kind === 'untagged'}
            onClick={() => onSelect({ kind: 'untagged' })}
          />
        </>
      )}
    </aside>
  );
}

function FolderRows({
  node,
  depth,
  selection,
  onSelect,
  isExpanded,
  onToggleExpand,
}: {
  node: FolderNode;
  depth: number;
  selection: MediaSelection;
  onSelect: (s: MediaSelection) => void;
  isExpanded: (path: string, depth: number) => boolean;
  onToggleExpand: (path: string, current: boolean) => void;
}): JSX.Element {
  const hasChildren = node.children.length > 0;
  const expanded = hasChildren && isExpanded(node.path, depth);

  return (
    <>
      <TreeRow
        label={node.name}
        icon="📁"
        count={node.total}
        depth={depth}
        active={selection.kind === 'folder' && selection.path === node.path}
        onClick={() => onSelect({ kind: 'folder', path: node.path })}
        {...(hasChildren
          ? { expanded, onToggleExpand: () => onToggleExpand(node.path, expanded) }
          : {})}
      />
      {expanded &&
        node.children.map((c) => (
          <FolderRows
            key={c.path}
            node={c}
            depth={depth + 1}
            selection={selection}
            onSelect={onSelect}
            isExpanded={isExpanded}
            onToggleExpand={onToggleExpand}
          />
        ))}
    </>
  );
}

function TreeRow({
  label,
  icon,
  count,
  depth,
  active,
  onClick,
  expanded,
  onToggleExpand,
}: {
  label: string;
  icon: string;
  count: number;
  depth: number;
  active: boolean;
  onClick: () => void;
  /** 有值 = 這個節點有子項，顯示三角形；undefined = 葉節點，留空位對齊。 */
  expanded?: boolean;
  onToggleExpand?: () => void;
}): JSX.Element {
  // 外層改用 div 而非把三角形塞進同一個 button —— button 內嵌 button 是無效
  // HTML，瀏覽器會自行拆解，點擊行為變得無法預期
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        background: active ? '#e0e7ff' : 'transparent',
        borderRadius: 4,
        paddingLeft: depth * 14,
      }}
    >
      {onToggleExpand ? (
        <button
          onClick={onToggleExpand}
          title={expanded ? '收合' : '展開'}
          aria-label={`${expanded ? '收合' : '展開'} ${label}`}
          aria-expanded={expanded}
          style={CARET}
        >
          {expanded ? '▾' : '▸'}
        </button>
      ) : (
        <span style={{ width: CARET_WIDTH, flexShrink: 0 }} />
      )}
      <button
        onClick={onClick}
        title={label}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flex: 1,
          minWidth: 0,
          border: 'none',
          background: 'transparent',
          color: active ? '#1e3a8a' : '#374151',
          fontWeight: active ? 600 : 400,
          padding: '4px 8px 4px 0',
          fontSize: 13,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span style={{ flexShrink: 0 }}>{icon}</span>
        <span
          style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {label}
        </span>
        <span style={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }}>{count}</span>
      </button>
    </div>
  );
}

/** 目前位置；路徑每一段都可點，用來往上跳。 */
export function MediaBreadcrumb({
  selection,
  onSelect,
}: {
  selection: MediaSelection;
  onSelect: (s: MediaSelection) => void;
}): JSX.Element {
  const crumb: React.ReactNode[] = [
    <button key="root" style={CRUMB_BTN} onClick={() => onSelect({ kind: 'all' })}>
      全部素材
    </button>,
  ];

  if (selection.kind === 'folder') {
    const segments = selection.path.split('/');
    segments.forEach((seg, i) => {
      const path = segments.slice(0, i + 1).join('/');
      const last = i === segments.length - 1;
      crumb.push(
        <span key={`sep${path}`} style={{ color: '#9ca3af' }}>
          ›
        </span>,
        <button
          key={path}
          style={{
            ...CRUMB_BTN,
            fontWeight: last ? 600 : 400,
            color: last ? '#111827' : '#2563eb',
          }}
          onClick={() => onSelect({ kind: 'folder', path })}
          disabled={last}
        >
          {seg}
        </button>,
      );
    });
  } else if (selection.kind !== 'all') {
    crumb.push(
      <span key="sep" style={{ color: '#9ca3af' }}>
        ›
      </span>,
      <span key="cur" style={{ fontWeight: 600 }}>
        {selection.kind === 'label' ? `標記：${selection.tag}` : '未分類'}
      </span>,
    );
  }

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14, minHeight: 26 }}>
      {crumb}
    </div>
  );
}

/**
 * 列上的標籤。路徑型（含 `/`）與平面標記分開呈現並用不同圖示 —— 在 `0/zh` 這種
 * 上層視圖裡，同名檔案唯一的區別就是這一行，所以它不能只是個灰字附註。
 */
export function MediaTagLine({
  tags,
  onFolder,
}: {
  tags: string[];
  onFolder: (s: MediaSelection) => void;
}): JSX.Element {
  if (tags.length === 0) return <div style={TAG_LINE}>—</div>;
  const folders = tags.filter((t) => t.includes('/'));
  const labels = tags.filter((t) => !t.includes('/'));
  return (
    <div style={TAG_LINE}>
      {folders.map((f) => (
        <button
          key={f}
          style={{ ...CRUMB_BTN, fontSize: 11, color: '#2563eb' }}
          onClick={() => onFolder({ kind: 'folder', path: f })}
          title="跳到這個資料夾"
        >
          📁 {f}
        </button>
      ))}
      {labels.length > 0 && <span>🏷 {labels.join('、')}</span>}
    </div>
  );
}

function Divider({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        fontSize: 11,
        color: '#9ca3af',
        padding: '10px 8px 2px',
        borderTop: '1px solid #e5e7eb',
        marginTop: 6,
      }}
    >
      {children}
    </div>
  );
}

/** 三角形欄寬；葉節點以同寬的空白佔位，各層的圖示才會對齊。 */
const CARET_WIDTH = 18;

const CARET: React.CSSProperties = {
  width: CARET_WIDTH,
  flexShrink: 0,
  border: 'none',
  background: 'none',
  color: '#6b7280',
  // 10px 下 ▾ 只剩一個雜點，看不出可以點；12 才像個控制項
  fontSize: 12,
  lineHeight: 1,
  padding: 0,
  cursor: 'pointer',
};

const TAG_LINE: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
  fontSize: 11,
  color: '#6b7280',
  marginTop: 3,
};

const CRUMB_BTN: React.CSSProperties = {
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: 0,
  font: 'inherit',
  color: '#2563eb',
};

const SIDEBAR: React.CSSProperties = {
  width: 240,
  flexShrink: 0,
  maxHeight: '70vh',
  overflowY: 'auto',
  paddingRight: 8,
  borderRight: '1px solid #e5e7eb',
};
